import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Code,
  Database,
  Webhook,
  Lock,
  ChevronDown,
  ArrowRight,
  Settings,
  DollarSign,
  Package,
  Users,
  Search,
  Calendar,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const endpointKeys = [
  { method: "GET",  endpoint: "/v1/hotel/configuration", key: "hotelConfig", icon: <Settings className="h-6 w-6" /> },
  { method: "GET",  endpoint: "/v1/rates",                key: "rates",       icon: <DollarSign className="h-6 w-6" /> },
  { method: "GET",  endpoint: "/v1/products",             key: "products",    icon: <Package className="h-6 w-6" /> },
  { method: "POST", endpoint: "/v1/guests/search",        key: "searchGuests",       icon: <Users className="h-6 w-6" /> },
  { method: "POST", endpoint: "/v1/reservations/search",  key: "searchReservations", icon: <Search className="h-6 w-6" /> },
  { method: "GET",  endpoint: "/v1/reservations/{id}",    key: "getReservation",     icon: <Calendar className="h-6 w-6" /> },
];

const featureKeys = [
  { icon: Code,     key: "restful" },
  { icon: Database, key: "sync" },
  { icon: Webhook,  key: "webhooks" },
  { icon: Lock,     key: "security" },
];

export function ApiFeatures() {
  const { t } = useTranslation();
  const [expandedEndpoint, setExpandedEndpoint] = useState<number | null>(null);

  const toggleEndpoint = (index: number) => {
    setExpandedEndpoint(expandedEndpoint === index ? null : index);
  };

  return (
    <div className="bg-gray-900 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl text-white mb-4">
            {t("apiFeatures.title")}
          </h2>
          <p className="text-xl text-gray-300">
            {t("apiFeatures.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* API Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 h-full">
            {featureKeys.map(({ icon: Icon, key }) => (
              <div key={key} className="bg-gray-800 rounded-lg p-6 h-full flex flex-col">
                <div className="flex items-center mb-4">
                  <div className="bg-[#9D72F7] rounded-lg p-3">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="ml-4 text-lg text-white">{t(`apiFeatures.features.${key}.title`)}</h3>
                </div>
                <p className="text-gray-400 flex-1">{t(`apiFeatures.features.${key}.description`)}</p>
              </div>
            ))}
          </div>

          {/* API Endpoints */}
          <div className="space-y-2">
            {endpointKeys.map(({ method, endpoint, key, icon }, index) => (
              <div key={index} className="bg-gray-800 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleEndpoint(index)}
                  className="w-full px-4 py-2 bg-gray-700 flex items-center justify-between hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-mono ${
                        method === "GET"
                          ? "bg-green-900 text-green-300"
                          : "bg-blue-900 text-blue-300"
                      }`}
                    >
                      {method}
                    </span>
                    <span className="text-gray-300 font-mono text-sm">{endpoint}</span>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedEndpoint === index ? "transform rotate-180" : ""
                    }`}
                  />
                </button>
                {expandedEndpoint === index && (
                  <div className="p-4">
                    <div className="flex items-center mb-3">
                      <div className="bg-[#9D72F7] rounded-lg p-2 mr-3">{icon}</div>
                      <h4 className="text-lg font-semibold text-white">
                        {t(`apiFeatures.endpoints.${key}.title`)}
                      </h4>
                    </div>
                    <p className="text-gray-400 mb-4">
                      {t(`apiFeatures.endpoints.${key}.description`)}
                    </p>
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <h5 className="text-sm font-semibold text-blue-400 mb-2">
                        {t("apiFeatures.keyBenefits")}
                      </h5>
                      <ul className="space-y-1">
                        {(t(`apiFeatures.endpoints.${key}.benefits`, { returnObjects: true }) as string[]).map(
                          (benefit, i) => (
                            <li key={i} className="text-sm text-gray-300 flex items-start">
                              <span className="text-blue-400 mr-2">•</span>
                              {benefit}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="text-center mt-8">
              <Link
                to="/api-documentation"
                className="inline-flex items-center text-[#9D72F7] hover:text-[#8B5CF6] font-medium"
              >
                {t("apiFeatures.fullDoc")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
